import { Host, h } from "@stencil/core";
export class ChAlert {
    constructor() {
        this.timerInterval = 50;
        this.closeButtonAccessibleName = "Close";
        this.dismissTimeout = 0;
        this.leftImgSrc = "";
        this.presented = false;
        this.showCloseButton = false;
        this.showTimeoutBar = false;
        this.pauseOnHover = true;
        this.countdown = this.dismissTimeout;
    }
    /** Sets the timer id */
    timerId = null;
    timeoutWatcher(newValue) {
        if (newValue) {
            this.countdown = newValue;
            this.start();
        }
    }
    presentedWatcher(newValue) {
        if (newValue) {
            this.start();
        }
    }
    /** Closes the alert when the close button is clicked.
     * Also restarts the counter and sets its value to match dismissTimeout.
     */
    handleAlertClose = () => {
        clearInterval(this.timerId);
        this.presented = false;
        this.close.emit();
        this.countdown = this.dismissTimeout;
    };
    /** Countdown watcher that hides the alert if dismissTimeout is reached
     * and stops the countdown.
     * See handleAlertClose for more details. */
    countdownWatcher(newValue) {
        if (newValue <= 0) {
            this.handleAlertClose();
        }
    }
    /** Fires close event */
    close;
    /** Counter decremental function */
    counter = () => {
        this.countdown -= this.timerInterval;
    };
    /** Starts a new countdown which interval is set in timerInterval,
     * Only if is presented, dismissTimeout is greater than 0,
     * and countdown is still running.
     */
    start = () => {
        clearInterval(this.timerId);
        if (this.presented && this.dismissTimeout !== 0 && this.countdown >= 0) {
            this.timerId = setInterval(this.counter, this.timerInterval);
        }
    };
    /** Pauses the countdown */
    handleMouseEnter = () => {
        clearInterval(this.timerId);
    };
    /** Resumes the countdown */
    handleMouseLeave = () => {
        this.start();
    };
    componentDidLoad() {
        this.start();
    }
    render() {
        return (h(Host, { role: "alert", onMouseEnter: this.pauseOnHover && this.handleMouseEnter, onMouseLeave: this.pauseOnHover && this.handleMouseLeave, "aria-hidden": !this.presented ? "true" : "false", class: this.pauseOnHover && "pause-on-hover" }, this.presented && [
            this.leftImgSrc && (h("img", { part: "image", class: "image", src: this.leftImgSrc, alt: "", "aria-hidden": "true", loading: "lazy" })),
            h("div", { part: "content", class: "content" }, h("slot", { name: "content" })),
            this.showCloseButton && (h("button", { part: "close-button", class: "close-button", type: "button", "aria-label": this.closeButtonAccessibleName, onClick: this.handleAlertClose }, h("slot", { name: "button", "aria-hidden": "true" }, h("div", { "aria-hidden": "true", class: "close-button-img" })))),
            this.showTimeoutBar && (h("ch-timer", { part: "indicator-container", class: "indicator-container", exportparts: "indicator", progress: (this.countdown * 100) / this.dismissTimeout, accessibleName: `${this.countdown / 1000} seconds left`, "animation-time": this.dismissTimeout, presented: this.presented }))
        ]));
    }
    static get is() { return "ch-alert"; }
    static get encapsulation() { return "shadow"; }
    static get originalStyleUrls() {
        return {
            "$": ["alert.scss"]
        };
    }
    static get styleUrls() {
        return {
            "$": ["alert.css"]
        };
    }
    static get properties() {
        return {
            "timerInterval": {
                "type": "number",
                "mutable": false,
                "complexType": {
                    "original": "50",
                    "resolved": "50",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Sets the desired interval"
                },
                "attribute": "timer-interval",
                "reflect": false,
                "defaultValue": "50"
            },
            "closeButtonAccessibleName": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Determine the accessible name of the close button.\nImportant for accessibility."
                },
                "attribute": "close-button-accessible-name",
                "reflect": false,
                "defaultValue": "\"Close\""
            },
            "dismissTimeout": {
                "type": "number",
                "mutable": false,
                "complexType": {
                    "original": "0",
                    "resolved": "0",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Specifies the time (ms) for the alert to be displayed.\nif `dismissTimeout = 0`, the alert will be always visible\n(unless is dismissed by the closeButton)."
                },
                "attribute": "dismiss-timeout",
                "reflect": false,
                "defaultValue": "0"
            },
            "leftImgSrc": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "\"\"",
                    "resolved": "\"\"",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Determine src of the left image."
                },
                "attribute": "left-img-src",
                "reflect": false,
                "defaultValue": "\"\""
            },
            "presented": {
                "type": "boolean",
                "mutable": true,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Determine if the element is displayed or not."
                },
                "attribute": "presented",
                "reflect": true,
                "defaultValue": "false"
            },
            "showCloseButton": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Determine if the closeButton is displayed or not."
                },
                "attribute": "show-close-button",
                "reflect": false,
                "defaultValue": "false"
            },
            "showTimeoutBar": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "If dismissTimeout > 0, a progress bar is displayed at the bottom of the element\nshowing the time left for the alert to show.\nThe progress stops when the element is hovered."
                },
                "attribute": "show-timeout-bar",
                "reflect": false,
                "defaultValue": "false"
            },
            "pauseOnHover": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Toggles the Pause on Hover functionality"
                },
                "attribute": "pause-on-hover",
                "reflect": false,
                "defaultValue": "true"
            },
            "countdown": {
                "type": "number",
                "mutable": true,
                "complexType": {
                    "original": "number",
                    "resolved": "number",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Countdown which initial state is dismissTimeout ms."
                },
                "attribute": "countdown",
                "reflect": false,
                "defaultValue": "this.dismissTimeout"
            }
        };
    }
    static get events() {
        return [{
                "method": "close",
                "name": "close",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": "Fires close event"
                },
                "complexType": {
                    "original": "any",
                    "resolved": "any",
                    "references": {}
                }
            }];
    }
    static get watchers() {
        return [{
                "propName": "dismissTimeout",
                "methodName": "timeoutWatcher"
            }, {
                "propName": "presented",
                "methodName": "presentedWatcher"
            }, {
                "propName": "countdown",
                "methodName": "countdownWatcher"
            }];
    }
}
//# sourceMappingURL=alert.js.map
